import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { BulkMembreData } from '@/types/bulk';
import { toast } from 'sonner';

interface UseCreateMembresReturn {
  createMembres: (membres: BulkMembreData[], entrepriseId: string) => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
}

export function useCreateMembres(): UseCreateMembresReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createMembres = async (membres: BulkMembreData[], entrepriseId: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // Préparer les données pour l'insertion (sans l'id temporaire)
      const membresToInsert = membres.map(({ id, ...membre }) => ({
        prenom: membre.prenom,
        nom: membre.nom,
        poste: membre.poste || null,
        specialite: membre.specialite || null,
        equipe_id: membre.equipe_id || null,
        taux_horaire: membre.taux_horaire,
        coefficient_charges: membre.coefficient_charges,
        heures_par_jour: membre.heures_par_jour,
        entreprise_id: entrepriseId,
        statut: 'disponible',
      }));

      const { data, error: insertError } = await supabase
        .from('membres_equipe')
        .insert(membresToInsert)
        .select();

      if (insertError) throw insertError;

      toast.success(`${membres.length} membre(s) ajouté(s) avec succès`);
      return true;
    } catch (err: any) {
      const message = err.message || 'Erreur lors de l\'ajout des membres';
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { createMembres, isLoading, error };
}
